import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInquiries,
  fetchInquiry,
  updateInquiry,
  sendInquiryEmail,
} from "../../../store/thunks/inquiriesActions";
import { applyView, computeAnalytics } from "../workspace/utils";

const INQUIRY_POLL_INTERVAL_MS = 15_000;

// Central orchestrator for the Inquiries workspace. Mirrors the pattern used
// by useLeadsWorkspace: a single hook owning fetch, filters, selection and
// derived data so the dashboard shell stays thin.
export default function useInquiriesWorkspace() {
  const dispatch = useDispatch();
  const { list: inquiries = [], current, loading } = useSelector(
    (s) => s.inquiries || {}
  );
  const adminInfo = useSelector((s) => s.login?.adminInfo || {});

  const [view, setView] = useState("today");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedId, setSelectedId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const lastFetchRef = useRef(0);
  const inFlightRefreshRef = useRef(null);

  const refresh = useCallback(
    async ({ quiet = false } = {}) => {
      if (inFlightRefreshRef.current) {
        if (!quiet) {
          setRefreshing(true);
          try {
            await inFlightRefreshRef.current;
          } finally {
            setRefreshing(false);
          }
        }
        return inFlightRefreshRef.current;
      }

      if (!quiet) {
        setRefreshing(true);
      }

      const request = (async () => {
        try {
          await dispatch(fetchInquiries());
          lastFetchRef.current = Date.now();
        } finally {
          inFlightRefreshRef.current = null;
          if (!quiet) {
            setRefreshing(false);
          }
        }
      })();

      inFlightRefreshRef.current = request;
      return request;
    },
    [dispatch]
  );

  useEffect(() => {
    refresh({ quiet: true });
  }, [refresh]);

  useEffect(() => {
    const shouldPoll = () =>
      typeof document === "undefined" || document.visibilityState === "visible";

    const poll = () => {
      if (!shouldPoll()) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      refresh({ quiet: true });
    };

    const intervalId = window.setInterval(poll, INQUIRY_POLL_INTERVAL_MS);
    const handleVisibility = () => {
      if (!shouldPoll()) return;
      if (Date.now() - lastFetchRef.current < 5_000) return;
      refresh({ quiet: true });
    };

    window.addEventListener("focus", handleVisibility);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleVisibility);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) return;
    dispatch(fetchInquiry(selectedId));
  }, [dispatch, selectedId]);

  const analytics = useMemo(() => computeAnalytics(inquiries), [inquiries]);

  const filtered = useMemo(
    () => applyView({ inquiries, view, search, typeFilter, sortBy }),
    [inquiries, view, search, typeFilter, sortBy]
  );

  const counts = useMemo(() => {
    const base = {
      today: 0,
      awaiting: 0,
      replied: 0,
      closed: 0,
      all: inquiries.length,
    };
    for (const i of inquiries) {
      if (i.closed) base.closed += 1;
      else if (i.replied) base.replied += 1;
      else base.awaiting += 1;
      if (
        !i.closed &&
        i.createdAt &&
        new Date(i.createdAt).toDateString() === new Date().toDateString()
      ) {
        base.today += 1;
      }
    }
    return base;
  }, [inquiries]);

  const selectInquiry = useCallback((id) => setSelectedId(id), []);
  const clearSelection = useCallback(() => setSelectedId(null), []);

  const patchInquiry = useCallback(
    (id, updates) => dispatch(updateInquiry(id, updates)),
    [dispatch]
  );

  const sendReply = useCallback(
    (payload) => dispatch(sendInquiryEmail(payload)),
    [dispatch]
  );

  const selectNext = useCallback(() => {
    if (!filtered.length) return;
    const idx = filtered.findIndex((i) => i._id === selectedId);
    const next = filtered[Math.min(idx + 1, filtered.length - 1)];
    if (next) setSelectedId(next._id);
  }, [filtered, selectedId]);

  const selectPrevious = useCallback(() => {
    if (!filtered.length) return;
    const idx = filtered.findIndex((i) => i._id === selectedId);
    const prev = filtered[Math.max(idx - 1, 0)];
    if (prev) setSelectedId(prev._id);
  }, [filtered, selectedId]);

  return {
    adminInfo,
    inquiries,
    filtered,
    loading,
    refreshing,
    current,
    analytics,
    counts,
    view,
    setView,
    typeFilter,
    setTypeFilter,
    search,
    setSearch,
    sortBy,
    setSortBy,
    selectedId,
    selectInquiry,
    clearSelection,
    selectNext,
    selectPrevious,
    refresh,
    patchInquiry,
    sendReply,
  };
}
